import * as React from 'react'
import { signOut } from 'next-auth/react'
import { useRouter } from 'next/router'
import Link from 'next/link'

import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'

import { HeaderBarHeightPixel } from 'components/atomic/organisms/header'
import { I18n, useTranslation } from 'components/hooks/use-translation'
import { useUser } from 'components/hooks/state'
import Drawer from 'components/atomic/atoms/drawer'
import Button from 'components/atomic/atoms/button'
import Icon from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'

import Routes from 'utils/routes'

const drawerWidth = 240

const Wrapper = styled(Box)`
    height: ${`calc(100vh - ${HeaderBarHeightPixel}px)`};
    width: 15rem;
    padding: 3rem 1rem 3rem 0rem;
    z-index: 200;
    position: sticky;
    top: 60px;
    overflow-y: scroll;
    margin-left: -0.5rem;
    background-color: ${({ theme }) => theme.palette.background.default};
    border-right: ${({ theme }) => `1px solid ${theme.palette.divider}`};
`

const StyledText = styled(Text)`
    margin-left: 0.5rem;
    margin-bottom: 0.5rem;
`

export const SidebarMobile = (props: SideBarMobileProps) => {
    const { appendRoutes } = props
    const router = useRouter()
    const { t }: I18n = useTranslation()
    const { user } = useUser()

    const isCurrent = React.useCallback((value: string) => router.asPath === value, [router.asPath])

    const routes = React.useMemo((): Routes => {
        let routes: Routes = []

        if (router.pathname.includes('settings')) {
            routes = [
                {
                    key: 'profile-routes-mobile',
                    rootLabel: t('settings:navTitle'),
                    routes: [
                        {
                            to: Routes.AccountSettings,
                            label: t(`settings:navLinks.account`),
                        },
                        {
                            to: Routes.PlanSettings,
                            label: t(`settings:navLinks.subscription`),
                        },
                    ],
                },
            ]
        }

        if (router.pathname.includes('catalog')) {
            routes = [
                {
                    key: 'catalog-routes-mobile',
                    routes: [
                        {
                            to: Routes.Catalog,
                            label: t('catalog:sidebar.allCatalog'),
                        },
                        {
                            to: Routes.CatalogAvailable,
                            label: t('catalog:sidebar.availableCatalog'),
                        },
                    ],
                },
            ]

            if (user) {
                routes[0].routes.push({
                    to: Routes.CatalogPersonal,
                    label: t('catalog:sidebar.myPaths'),
                })
            }
        }

        return routes.concat(appendRoutes || [])
    }, [router.pathname, t, appendRoutes, user])

    return (
        <Drawer open={props.isOpen} onClose={props.onClose} anchor="left">
            <Stack gap="2rem" p={2}>
                {routes.map((item) => {
                    return (
                        <Stack key={item.key || item.rootLabel} gap="0rem">
                            {item.rootLabel ? <StyledText variant="h6">{item.rootLabel}</StyledText> : null}
                            {item.routes.map((route) => {
                                const selected = isCurrent(route.to)
                                return (
                                    <Link key={route.to} href={route.to} passHref>
                                        <a>
                                            <Button
                                                sx={{ justifyContent: 'start' }}
                                                endIcon={
                                                    selected ? (
                                                        <Icon color="primary" fontSize="small" name="rightArrow" />
                                                    ) : null
                                                }>
                                                <Text
                                                    fontWeight={700}
                                                    variant="body2"
                                                    color={selected ? 'primary' : 'text.secondary'}>
                                                    {route.label}
                                                </Text>
                                            </Button>
                                        </a>
                                    </Link>
                                )
                            })}
                        </Stack>
                    )
                })}
            </Stack>
        </Drawer>
    )
}

const SideBar = React.memo((props: SideBarProps) => {
    const { appendRoutes } = props
    const router = useRouter()
    const { t }: I18n = useTranslation()
    const { user } = useUser()

    const isCurrent = React.useCallback((value: string) => router.asPath === value, [router.asPath])

    const routes = React.useMemo((): Routes => {
        let routes: Routes = []

        if (router.pathname.includes('settings')) {
            routes = [
                {
                    key: 'profile-routes',
                    rootLabel: t('settings:navTitle'),
                    routes: [
                        {
                            to: Routes.AccountSettings,
                            label: t(`settings:navLinks.account`),
                        },
                        {
                            to: Routes.PlanSettings,
                            label: t(`settings:navLinks.subscription`),
                        },
                    ],
                },
            ]
        }

        if (router.pathname.includes('catalog')) {
            routes = [
                {
                    key: 'catalog-routes',
                    routes: [
                        {
                            to: Routes.Catalog,
                            label: t('catalog:sidebar.allCatalog'),
                        },
                        {
                            to: Routes.CatalogAvailable,
                            label: t('catalog:sidebar.availableCatalog'),
                        },
                    ],
                },
            ]

            if (user) {
                routes[0].routes.push({
                    to: Routes.CatalogPersonal,
                    label: t('catalog:sidebar.myPaths'),
                })
            }
        }

        return routes.concat(appendRoutes || [])
    }, [router.pathname, t, appendRoutes, user])

    return (
        <Box component="nav" sx={{ width: { md: drawerWidth }, display: { xs: 'none', md: 'block' } }}>
            <Wrapper>
                <Stack gap="2rem">
                    {routes.map((item) => {
                        return (
                            <Stack key={item.key || item.rootLabel} gap="0">
                                {item.rootLabel ? <StyledText variant="h6">{item.rootLabel}</StyledText> : null}
                                {item.routes.map((route) => {
                                    const selected = isCurrent(route.to)
                                    return (
                                        <Link key={route.to} href={route.to} passHref>
                                            <a>
                                                <Button
                                                    sx={{ justifyContent: 'start' }}
                                                    endIcon={
                                                        selected ? (
                                                            <Icon color="primary" fontSize="small" name="rightArrow" />
                                                        ) : null
                                                    }>
                                                    <Text
                                                        fontWeight={700}
                                                        variant="body2"
                                                        color={selected ? 'primary' : 'text.secondary'}
                                                        textAlign="left">
                                                        {route.label}
                                                    </Text>
                                                </Button>
                                            </a>
                                        </Link>
                                    )
                                })}
                            </Stack>
                        )
                    })}
                    {router.pathname.includes('settings') ? (
                        <div>
                            <Button variant="outlined" onClick={() => signOut()}>
                                {t('common:buttons.logout')}
                            </Button>
                        </div>
                    ) : null}
                </Stack>
            </Wrapper>
        </Box>
    )
})

export default SideBar
